import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Tag, Truck, ShieldCheck, ZoomIn, X } from 'lucide-react';
import './Cart.css';

function Cart({ cartItems, removeFromCart, updateCartQuantity }) {
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);

  const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const discount = promoApplied ? subtotal * 0.10 : 0;
  const afterDiscount = subtotal - discount;
  const shipping = afterDiscount > 500 ? 0 : 50;
  const tax = afterDiscount * 0.15;
  const total = afterDiscount + shipping + tax;

  const handlePromoApply = (e) => {
    e.preventDefault();
    if (promoCode.trim().toUpperCase() === 'WELCOME10') {
      setPromoApplied(true);
      alert('Promo code applied! 10% discount added.');
    } else {
      alert('Invalid promo code. Try: WELCOME10');
    }
  };

  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="cart">
      {/* Zoom Lightbox Modal */}
      {zoomedImage && (
        <div className="zoom-modal-overlay" onClick={() => setZoomedImage(null)}>
          <div className="zoom-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="zoom-modal-close" onClick={() => setZoomedImage(null)}>
              <X size={28} />
            </button>
            <img src={zoomedImage.url} alt={zoomedImage.title} className="zoomed-image-display" />
            <div className="zoom-caption">{zoomedImage.title}</div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <section className="cart-header">
        <div className="cart-header-content">
          <h1>Shopping Cart</h1>
          <p>Review your items before checkout</p>
          <div className="breadcrumb">
            <span>Home</span> <span>/</span> <span className="active">Cart</span>
          </div>
        </div>
      </section>

      <section className="cart-content">
        {cartItems.length === 0 ? (
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <ShoppingBag size={64} />
            </div>
            <h2>Your cart is empty</h2>
            <p>Discover our collection of premium, ethically sourced essentials</p>
            <Link to="/collection" className="btn btn-gold">
              Start Shopping <ArrowRight size={18} />
            </Link>
          </div>
        ) : (
          <div className="cart-layout">
            {/* Cart Items */}
            <div className="cart-items-section">
              <div className="cart-items-header">
                <h2>Cart Items ({itemCount})</h2>
                <Link to="/collection" className="continue-link">
                  ← Continue Shopping
                </Link>
              </div>

              <div className="cart-items-list">
                {cartItems.map(item => (
                  <div key={item.id} className="cart-item-row">
                    {/* Large Image Container with Zoom Facility */}
                    <div className="cart-item-large-image" onClick={() => setZoomedImage({ url: item.image, title: item.name })}>
                      <img src={item.image} alt={item.name} />
                      <div className="image-zoom-overlay">
                        <ZoomIn size={20} />
                        <span>Zoom</span>
                      </div>
                    </div>

                    <div className="cart-item-info">
                      <h3 className="cart-item-name">{item.name}</h3>
                      <p className="cart-item-desc">{item.description || 'Authentic premium quality item from Medina'}</p>
                      {item.badge && <span className="cart-item-badge">{item.badge}</span>}
                    </div>

                    <div className="cart-item-price">
                      <span className="price-label">Price</span>
                      <span className="price-value">{item.price} SAR</span>
                    </div>

                    <div className="cart-item-quantity">
                      <span className="qty-label">Quantity</span>
                      <div className="quantity-control">
                        <button
                          className="qty-btn"
                          onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="qty-value">{item.quantity}</span>
                        <button
                          className="qty-btn"
                          onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="cart-item-total">
                      <span className="total-label">Total</span>
                      <span className="total-value">{(item.price * item.quantity).toFixed(0)} SAR</span>
                    </div>

                    <button
                      className="cart-item-remove"
                      onClick={() => removeFromCart(item.id)}
                      title="Remove item"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Trust badges */}
              <div className="cart-trust-badges">
                <div className="trust-badge">
                  <ShieldCheck size={20} />
                  <span>Secure Checkout</span>
                </div>
                <div className="trust-badge">
                  <Truck size={20} />
                  <span>Fast Delivery</span>
                </div>
                <div className="trust-badge">
                  <Tag size={20} />
                  <span>Best Prices</span>
                </div>
              </div>
            </div>

            {/* Cart Summary */}
            <div className="cart-summary-section">
              <div className="cart-summary">
                <h3>Order Summary</h3>

                {/* Promo Code */}
                <form className="promo-code-box" onSubmit={handlePromoApply}>
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                  />
                  <button type="submit" className="btn-apply-promo">Apply</button>
                </form>
                {promoApplied && (
                  <p className="promo-success">✓ WELCOME10 applied - 10% off!</p>
                )}

                <div className="summary-row">
                  <span>Subtotal ({itemCount} items)</span>
                  <span>{subtotal.toFixed(0)} SAR</span>
                </div>

                {promoApplied && (
                  <div className="summary-row discount">
                    <span>Discount (10%)</span>
                    <span>-{discount.toFixed(0)} SAR</span>
                  </div>
                )}

                <div className="summary-row">
                  <span>Shipping</span>
                  <span className={shipping === 0 ? 'free' : ''}>
                    {shipping === 0 ? 'FREE' : `${shipping} SAR`}
                  </span>
                </div>

                <div className="summary-row">
                  <span>Tax (15%)</span>
                  <span>{tax.toFixed(2)} SAR</span>
                </div>

                <div className="summary-divider"></div>

                <div className="summary-row total">
                  <span>Total</span>
                  <span>{total.toFixed(2)} SAR</span>
                </div>

                {subtotal <= 500 && (
                  <div className="free-shipping-progress">
                    <p>Add <strong>{(500 - subtotal).toFixed(0)} SAR</strong> more for FREE shipping!</p>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${Math.min((subtotal / 500) * 100, 100)}%` }}></div>
                    </div>
                  </div>
                )}

                <button className="btn btn-gold btn-checkout" onClick={() => navigate('/checkout')}>
                  Proceed to Checkout <ArrowRight size={18} />
                </button>

                <div className="payment-methods">
                  <span>We Accept:</span>
                  <div className="payment-icons">
                    <span className="pay-icon">💳 Visa</span>
                    <span className="pay-icon">💳 Mastercard</span>
                    <span className="pay-icon">🍎 Apple Pay</span>
                    <span className="pay-icon">💰 Cash</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default Cart;
